import React from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  ColDefBuilder,
  DataTable,
  Stack,
  Tag,
  Text,
  makeDefaultSortableColumnDef,
} from '@eluve/components';
import { useMedplumPatientAccess } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

import { carePlanPlanTitle } from './carePlanFhir';
import { usePatientCarePlans } from './usePatientCarePlans';

type CarePlanRow = {
  id: string;
  title: string;
  status: string;
  started: string;
};

const columns = new ColDefBuilder<CarePlanRow>()
  .colDef({
    ...makeDefaultSortableColumnDef('title', 'Plan'),
    cell: ({ row }) => (
      <Link
        to={row.original.id}
        className="text-sm font-medium text-text-accent-secondary hover:underline"
      >
        {row.original.title}
      </Link>
    ),
  })
  .colDef({
    ...makeDefaultSortableColumnDef('status', 'Status'),
    cell: ({ row }) => {
      const { status } = row.original;
      return (
        <Tag
          label={status}
          color={status === 'active' ? 'lightgreen' : 'gray'}
        />
      );
    },
  })
  .dateSortable('started', 'Started')
  .build();

export const PatientCarePlans: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const isCarePlansEnabled = useIsPostHogFeatureFlagEnabled('CARE_PLANS');

  const {
    isLoading: isAccessLoading,
    hasAccess,
    fhirPatientId,
  } = useMedplumPatientAccess(patientId);
  const { carePlans, isLoading, isError } = usePatientCarePlans(fhirPatientId);

  if (!isCarePlansEnabled) return null;

  if (isAccessLoading) {
    return <Text color="tertiary">Checking access…</Text>;
  }

  if (!hasAccess) {
    return (
      <Text color="tertiary">
        No FHIR record is visible for this patient, so no care plans can be
        shown.
      </Text>
    );
  }

  if (isLoading) {
    return <Text color="tertiary">Loading care plans…</Text>;
  }

  if (isError) {
    return (
      <Text color="tertiary">
        Something went wrong loading this patient&apos;s care plans.
      </Text>
    );
  }

  if (carePlans.length === 0) {
    return <Text color="tertiary">No care plans for this patient.</Text>;
  }

  const rows = carePlans.map<CarePlanRow>((cp) => ({
    id: cp.id,
    title: carePlanPlanTitle(cp),
    status: cp.status ?? 'unknown',
    started: cp.period?.start ?? '',
  }));

  return (
    <Stack gap={3} className="pt-2">
      <DataTable
        columns={columns}
        data={rows}
        initialSortingState={[{ id: 'started', desc: true }]}
      />
    </Stack>
  );
};
